import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import toast from "react-hot-toast";

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    icon: "🕐",
    step: 0,
  },
  confirmed: {
    label: "Confirmed",
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: "✅",
    step: 1,
  },
  preparing: {
    label: "Preparing",
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    icon: "👨‍🍳",
    step: 2,
  },
  shipped: {
    label: "Shipped",
    color: "#f97316",
    bg: "#fff7ed",
    border: "#fed7aa",
    icon: "📦",
    step: 3,
  },
  out_for_delivery: {
    label: "Out for Delivery",
    color: "#06b6d4",
    bg: "#ecfeff",
    border: "#a5f3fc",
    icon: "🚴",
    step: 4,
  },
  delivered: {
    label: "Delivered",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    icon: "🎉",
    step: 5,
  },
  cancelled: {
    label: "Cancelled",
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    icon: "✕",
    step: -1,
  },
};

const ALL_STEPS = [
  { status: "pending", label: "Order Placed", icon: "🛒" },
  { status: "confirmed", label: "Confirmed", icon: "✅" },
  { status: "preparing", label: "Preparing", icon: "👨‍🍳" },
  { status: "shipped", label: "Shipped", icon: "📦" },
  { status: "out_for_delivery", label: "Out for Delivery", icon: "🚴" },
  { status: "delivered", label: "Delivered", icon: "🎉" },
];

const POLL_MS = 15000;

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const timeAgo = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 10) return "Just now";
  if (s < 60) return `${s}s ago`;
  return `${Math.floor(s / 60)}m ago`;
};

export default function OrderHistoryPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelModal, setCancelModal] = useState(null); // order object
  const [cancelReason, setCancelReason] = useState("");
  const [filter, setFilter] = useState("all");
  const timerRef = useRef(null);

  const fetchOrders = useCallback(async (silent = false) => {
    if (silent) setSyncing(true);
    else setLoading(true);
    try {
      const res = await api.get("/orders/my-orders");
      const next = res.data || [];
      setOrders((prev) =>
        JSON.stringify(prev) !== JSON.stringify(next) ? next : prev,
      );
      setLastUpdated(new Date());
    } catch {
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(false);
    timerRef.current = setInterval(() => fetchOrders(true), POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchOrders]);

  const openCancelModal = (order) => {
    setCancelModal(order);
    setCancelReason("");
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setCancelling(cancelModal._id);
    try {
      await api.patch(`/orders/${cancelModal._id}/cancel`, {
        reason: cancelReason.trim(),
      });
      toast.success("Order cancelled");
      setCancelModal(null);
      fetchOrders(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Cannot cancel this order");
    } finally {
      setCancelling(null);
    }
  };

  const filtered =
    filter === "all" ? orders : orders.filter((o) => o.orderStatus === filter);
  const counts = orders.reduce((acc, o) => {
    acc[o.orderStatus] = (acc[o.orderStatus] || 0) + 1;
    return acc;
  }, {});
  const activeCount = orders.filter(
    (o) => !["delivered", "cancelled"].includes(o.orderStatus),
  ).length;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f8fafb",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <Navbar />

      {/* ── Hero ── */}
      <div
        style={{
          background:
            "linear-gradient(135deg,#0a3d1f 0%,#0f5230 50%,#1a7040 100%)",
          padding: "28px 20px 24px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(76,175,114,0.08)",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 1152, margin: "0 auto", position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h1
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "clamp(20px,3vw,26px)",
                  margin: "0 0 4px",
                  letterSpacing: "-0.4px",
                }}
              >
                My Orders 📦
              </h1>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <p
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: 12,
                    margin: 0,
                  }}
                >
                  {orders.length} order{orders.length !== 1 ? "s" : ""} total
                  {activeCount > 0 && ` · ${activeCount} active`}
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 100,
                    padding: "3px 10px",
                  }}
                >
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: syncing ? "#fbbf24" : "#86efac",
                      animation: "blink 2s ease infinite",
                    }}
                  />
                  <span
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {syncing ? "Updating…" : `Updated ${timeAgo(lastUpdated)}`}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/products")}
              style={{
                padding: "8px 20px",
                borderRadius: 100,
                border: "1.5px solid rgba(255,255,255,0.3)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              + New Order
            </button>
          </div>
        </div>
      </div>

      {/* ── Filter tabs ── */}
      {orders.length > 0 && (
        <div
          style={{
            background: "#fff",
            borderBottom: "1px solid #f0f0f0",
            boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            position: "sticky",
            top: 64,
            zIndex: 30,
          }}
        >
          <div
            style={{
              maxWidth: 1152,
              margin: "0 auto",
              padding: "0 20px",
              display: "flex",
              gap: 4,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {[
              { key: "all", label: "All", count: orders.length },
              { key: "pending", label: "Pending", count: counts.pending || 0 },
              {
                key: "confirmed",
                label: "Confirmed",
                count: counts.confirmed || 0,
              },
              {
                key: "preparing",
                label: "Preparing",
                count: counts.preparing || 0,
              },
              {
                key: "out_for_delivery",
                label: "Out for Del.",
                count: counts.out_for_delivery || 0,
              },
              {
                key: "delivered",
                label: "Delivered",
                count: counts.delivered || 0,
              },
              {
                key: "cancelled",
                label: "Cancelled",
                count: counts.cancelled || 0,
              },
            ]
              .filter((t) => t.count > 0 || t.key === "all")
              .map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  style={{
                    padding: "10px 14px",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontSize: 12.5,
                    fontWeight: filter === tab.key ? 800 : 500,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    color: filter === tab.key ? "#2D8B4E" : "#6b7280",
                    borderBottom:
                      filter === tab.key
                        ? "2.5px solid #2D8B4E"
                        : "2.5px solid transparent",
                    transition: "all 0.15s",
                  }}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span
                      style={{
                        marginLeft: 5,
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "1px 6px",
                        borderRadius: 20,
                        background:
                          filter === tab.key
                            ? "rgba(45,139,78,0.12)"
                            : "#f3f4f6",
                        color: filter === tab.key ? "#2D8B4E" : "#9ca3af",
                      }}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
          </div>
        </div>
      )}

      <div
        style={{
          flex: 1,
          maxWidth: 1152,
          margin: "0 auto",
          padding: "20px 20px 48px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Loading */}
        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "60px 0",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "3px solid #e5e7eb",
                borderTopColor: "#2D8B4E",
                animation: "spin 0.7s linear infinite",
              }}
            />
          </div>
        )}

        {/* Empty */}
        {!loading && orders.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>📭</div>
            <h3
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#111827",
                margin: "0 0 8px",
              }}
            >
              No orders yet
            </h3>
            <p style={{ color: "#9ca3af", marginBottom: 24, fontSize: 14 }}>
              Place your first order to see it here!
            </p>
            <button
              onClick={() => navigate("/products")}
              style={{
                padding: "11px 28px",
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              Browse Menu
            </button>
          </div>
        )}

        {/* No filter results */}
        {!loading && orders.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
            <p style={{ fontWeight: 700, color: "#374151" }}>
              No {filter} orders
            </p>
            <button
              onClick={() => setFilter("all")}
              style={{
                marginTop: 10,
                fontSize: 13,
                color: "#2D8B4E",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Show all orders
            </button>
          </div>
        )}

        {/* Orders */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {filtered.map((order) => {
              const cfg =
                STATUS_CONFIG[order.orderStatus] || STATUS_CONFIG.pending;
              const isExp = expanded === order._id;
              const canCancel = ["pending", "confirmed"].includes(
                order.orderStatus,
              );
              const itemTotal =
                order.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0;
              const history = order.statusHistory || [];
              const histMap = {};
              history.forEach((h) => {
                histMap[h.status] = h;
              });

              return (
                <div
                  key={order._id}
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                  }}
                >
                  {/* colored top bar */}
                  <div style={{ height: 3, background: cfg.color }} />

                  {/* ── Admin note banner — shown when admin has left a message ── */}
                  {order.adminNote && (
                    <div
                      style={{
                        margin: "0",
                        padding: "9px 16px",
                        background: "rgba(245,158,11,0.08)",
                        borderBottom: "1px solid rgba(245,158,11,0.2)",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}
                      >
                        📢
                      </span>
                      <div>
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: "#d97706",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            margin: "0 0 2px",
                          }}
                        >
                          Message from ProteinSpot
                        </p>
                        <p
                          style={{
                            fontSize: 12.5,
                            color: "#374151",
                            margin: 0,
                            lineHeight: 1.5,
                          }}
                        >
                          {order.adminNote}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── COMPACT HEADER ── */}
                  <div
                    style={{
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    {/* status icon */}
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 17,
                        background: cfg.bg,
                        border: `1.5px solid ${cfg.border}`,
                        flexShrink: 0,
                        animation: !["delivered", "cancelled"].includes(
                          order.orderStatus,
                        )
                          ? "pulse-ring 2.5s ease infinite"
                          : "none",
                      }}
                    >
                      {cfg.icon}
                    </div>

                    {/* order id + status + current status time */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 900,
                            color: "#111827",
                          }}
                        >
                          #{order.orderId}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 100,
                            background: cfg.bg,
                            color: cfg.color,
                            border: `1px solid ${cfg.border}`,
                          }}
                        >
                          {cfg.label}
                        </span>
                      </div>
                      <div
                        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                      >
                        {/* current status timestamp — most useful info */}
                        {histMap[order.orderStatus] && (
                          <span
                            style={{
                              fontSize: 10,
                              color: cfg.color,
                              fontWeight: 600,
                            }}
                          >
                            🕐 {fmtDate(histMap[order.orderStatus].timestamp)}
                          </span>
                        )}
                        <span style={{ fontSize: 10, color: "#9ca3af" }}>
                          🛍 {order.items?.length} item
                          {order.items?.length !== 1 ? "s" : ""}
                        </span>
                        {order.paymentMethod && (
                          <span style={{ fontSize: 10, color: "#9ca3af" }}>
                            💳 {order.paymentMethod}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* right side: total + cancel + chevron */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexShrink: 0,
                      }}
                    >
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 900,
                            color: "#15803d",
                            letterSpacing: "-0.3px",
                            lineHeight: 1,
                          }}
                        >
                          ₹{order.total}
                        </div>
                        {order.deliveryFee > 0 && (
                          <div
                            style={{
                              fontSize: 9,
                              color: "#9ca3af",
                              marginTop: 1,
                            }}
                          >
                            +₹{order.deliveryFee} del.
                          </div>
                        )}
                      </div>

                      {/* Cancel button — shown for non-terminal statuses, disabled after confirming */}
                      {!["delivered", "cancelled"].includes(
                        order.orderStatus,
                      ) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canCancel) openCancelModal(order);
                          }}
                          disabled={!canCancel}
                          title={
                            !canCancel
                              ? "Cannot cancel once order is being prepared"
                              : "Cancel this order"
                          }
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: canCancel ? "#ef4444" : "#d1d5db",
                            background: "none",
                            border: `1px solid ${canCancel ? "#fecaca" : "#e5e7eb"}`,
                            borderRadius: 100,
                            padding: "4px 10px",
                            cursor: canCancel ? "pointer" : "not-allowed",
                            transition: "all 0.15s",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {canCancel ? "Cancel" : "Cannot Cancel"}
                        </button>
                      )}

                      <button
                        onClick={() => setExpanded(isExp ? null : order._id)}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: "1.5px solid #e5e7eb",
                          background: isExp ? "#f0fdf4" : "#fafafa",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#2D8B4E",
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            transform: isExp ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        >
                          ▾
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* item thumbnails strip */}
                  <div
                    style={{
                      padding: "0 16px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {order.items?.slice(0, 5).map((item, i) => (
                      <img
                        key={i}
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          objectFit: "cover",
                          border: `1.5px solid ${item.removed ? "#fecaca" : "#f0f0f0"}`,
                          flexShrink: 0,
                          filter: item.removed ? "grayscale(1)" : "none",
                          opacity: item.removed ? 0.5 : 1,
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/34/e8f5e9/2D8B4E?text=🥤";
                        }}
                        title={
                          item.removed
                            ? `${item.name} (Unavailable)`
                            : item.name
                        }
                      />
                    ))}
                    {(order.items?.length || 0) > 5 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          fontWeight: 600,
                        }}
                      >
                        +{order.items.length - 5}
                      </span>
                    )}
                    {!isExp && (
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 10,
                          color: "#d1d5db",
                        }}
                      >
                        tap ▾ for details
                      </span>
                    )}
                  </div>

                  {/* ══ EXPANDED SECTION ══ */}
                  {isExp && (
                    <div style={{ borderTop: "1px solid #f5f5f5" }}>
                      {/* Status timeline */}
                      <div
                        style={{
                          padding: "14px 16px 10px",
                          borderBottom: "1px solid #f5f5f5",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#9ca3af",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            margin: "0 0 12px",
                          }}
                        >
                          Status History
                        </p>

                        {order.orderStatus === "cancelled" ? (
                          /* cancelled — show only what happened */
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            {history.map((h, i) => {
                              const hcfg =
                                STATUS_CONFIG[h.status] ||
                                STATUS_CONFIG.pending;
                              const isLast = i === history.length - 1;
                              return (
                                <div
                                  key={i}
                                  style={{ display: "flex", gap: 10 }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 11,
                                        background: hcfg.bg,
                                        border: `2px solid ${hcfg.border}`,
                                        color: hcfg.color,
                                        fontWeight: 900,
                                      }}
                                    >
                                      {hcfg.icon}
                                    </div>
                                    {!isLast && (
                                      <div
                                        style={{
                                          width: 2,
                                          flex: 1,
                                          minHeight: 14,
                                          background: "#f0f0f0",
                                          margin: "2px 0",
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      paddingBottom: isLast ? 0 : 10,
                                      paddingTop: 2,
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: hcfg.color,
                                        margin: 0,
                                      }}
                                    >
                                      {hcfg.label}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: 10,
                                        color: "#9ca3af",
                                        margin: "1px 0 0",
                                      }}
                                    >
                                      {fmtDate(h.timestamp)}
                                    </p>
                                    {h.note && (
                                      <p
                                        style={{
                                          fontSize: 10,
                                          color: "#6b7280",
                                          margin: "1px 0 0",
                                        }}
                                      >
                                        {h.note}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          /* active — show all steps, greyed if not reached */
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            {ALL_STEPS.map((step, i) => {
                              const scfg =
                                STATUS_CONFIG[step.status] ||
                                STATUS_CONFIG.pending;
                              const h = histMap[step.status];
                              const isDone = !!h;
                              const isNow = order.orderStatus === step.status;
                              const isLast = i === ALL_STEPS.length - 1;
                              return (
                                <div
                                  key={step.status}
                                  style={{
                                    display: "flex",
                                    gap: 10,
                                    opacity: isDone ? 1 : 0.35,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 11,
                                        background: isDone
                                          ? scfg.bg
                                          : "#f9fafb",
                                        border: `2px solid ${isDone ? scfg.border : "#e5e7eb"}`,
                                        color: isDone ? scfg.color : "#d1d5db",
                                        fontWeight: 900,
                                        boxShadow: isNow
                                          ? `0 0 0 3px ${scfg.color}22`
                                          : "none",
                                        animation: isNow
                                          ? "pulse-ring 2s ease infinite"
                                          : "none",
                                      }}
                                    >
                                      {isDone && !isNow ? "✓" : step.icon}
                                    </div>
                                    {!isLast && (
                                      <div
                                        style={{
                                          width: 2,
                                          flex: 1,
                                          minHeight: 14,
                                          background: isDone
                                            ? `${scfg.color}30`
                                            : "#f0f0f0",
                                          margin: "2px 0",
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      paddingBottom: isLast ? 0 : 10,
                                      paddingTop: 2,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 5,
                                      }}
                                    >
                                      <p
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 700,
                                          color: isNow
                                            ? scfg.color
                                            : isDone
                                              ? "#374151"
                                              : "#d1d5db",
                                          margin: 0,
                                        }}
                                      >
                                        {step.label}
                                      </p>
                                      {isNow && (
                                        <span
                                          style={{
                                            fontSize: 9,
                                            fontWeight: 800,
                                            padding: "1px 6px",
                                            borderRadius: 20,
                                            background: `${scfg.color}15`,
                                            color: scfg.color,
                                          }}
                                        >
                                          NOW
                                        </span>
                                      )}
                                    </div>
                                    <p
                                      style={{
                                        fontSize: 10,
                                        color: isDone ? "#9ca3af" : "#d1d5db",
                                        margin: "1px 0 0",
                                      }}
                                    >
                                      {h ? fmtDate(h.timestamp) : "Pending"}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* All items — removed items shown with strikethrough */}
                      <div
                        style={{
                          padding: "14px 16px",
                          borderBottom: "1px solid #f5f5f5",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <p
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              margin: 0,
                            }}
                          >
                            Order Items
                          </p>
                          {order.items?.some((it) => it.removed) && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#ef4444",
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                padding: "2px 8px",
                                borderRadius: 100,
                              }}
                            >
                              {order.items.filter((it) => it.removed).length}{" "}
                              item
                              {order.items.filter((it) => it.removed).length > 1
                                ? "s"
                                : ""}{" "}
                              removed
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {order.items?.map((item, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "8px 10px",
                                background: item.removed
                                  ? "#fef2f2"
                                  : "#f9fafb",
                                borderRadius: 10,
                                border: `1px solid ${item.removed ? "#fecaca" : "#f0f0f0"}`,
                                opacity: item.removed ? 0.75 : 1,
                              }}
                            >
                              <img
                                src={item.image}
                                alt={item.name}
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                  flexShrink: 0,
                                  filter: item.removed
                                    ? "grayscale(1)"
                                    : "none",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/40/e8f5e9/2D8B4E?text=🥤";
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: item.removed
                                        ? "#9ca3af"
                                        : "#111827",
                                      margin: 0,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      textDecoration: item.removed
                                        ? "line-through"
                                        : "none",
                                    }}
                                  >
                                    {item.name}
                                  </p>
                                  {item.removed && (
                                    <span
                                      style={{
                                        fontSize: 9,
                                        fontWeight: 800,
                                        color: "#ef4444",
                                        background: "#fee2e2",
                                        padding: "1px 6px",
                                        borderRadius: 20,
                                        flexShrink: 0,
                                      }}
                                    >
                                      Unavailable
                                    </span>
                                  )}
                                </div>
                                <p
                                  style={{
                                    fontSize: 11,
                                    color: "#9ca3af",
                                    margin: "2px 0 0",
                                    textDecoration: item.removed
                                      ? "line-through"
                                      : "none",
                                  }}
                                >
                                  ₹{item.price} × {item.quantity}
                                </p>
                              </div>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 900,
                                  color: item.removed ? "#9ca3af" : "#15803d",
                                  flexShrink: 0,
                                  textDecoration: item.removed
                                    ? "line-through"
                                    : "none",
                                }}
                              >
                                ₹{item.price * item.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Bill summary */}
                      <div
                        style={{
                          padding: "14px 16px",
                          borderBottom: "1px solid #f5f5f5",
                          background: "rgba(45,139,78,0.02)",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#9ca3af",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            margin: "0 0 8px",
                          }}
                        >
                          Bill Summary
                        </p>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 5,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#6b7280" }}>
                              Item Total
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#374151",
                              }}
                            >
                              ₹{itemTotal}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#6b7280" }}>
                              Delivery Fee
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#374151",
                              }}
                            >
                              {order.deliveryFee > 0
                                ? `₹${order.deliveryFee}`
                                : "Free"}
                            </span>
                          </div>
                          {order.discount > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                Discount
                              </span>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#16a34a",
                                }}
                              >
                                -₹{order.discount}
                              </span>
                            </div>
                          )}
                          <div
                            style={{
                              height: 1,
                              background: "rgba(45,139,78,0.12)",
                              margin: "3px 0",
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: "#111827",
                              }}
                            >
                              Total Paid
                            </span>
                            <span
                              style={{
                                fontSize: 15,
                                fontWeight: 900,
                                color: "#15803d",
                              }}
                            >
                              ₹{order.total}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Delivery address + contact */}
                      <div
                        style={{
                          padding: "14px 16px",
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            background: "#f9fafb",
                            borderRadius: 10,
                            padding: "10px 12px",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              margin: "0 0 5px",
                            }}
                          >
                            📍 Delivery Address
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              color: "#374151",
                              margin: 0,
                              lineHeight: 1.5,
                            }}
                          >
                            {order.customerDetails?.name && (
                              <span
                                style={{ fontWeight: 700, display: "block" }}
                              >
                                {order.customerDetails.name}
                              </span>
                            )}
                            {order.customerDetails?.address?.street}
                            <br />
                            {order.customerDetails?.address?.city}
                            {order.customerDetails?.address?.pincode &&
                              ` - ${order.customerDetails.address.pincode}`}
                          </p>
                        </div>
                        <div
                          style={{
                            background: "#f9fafb",
                            borderRadius: 10,
                            padding: "10px 12px",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              margin: "0 0 5px",
                            }}
                          >
                            📞 Contact
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              color: "#374151",
                              margin: 0,
                              lineHeight: 1.6,
                            }}
                          >
                            {order.customerDetails?.phone && (
                              <span
                                style={{ display: "block", fontWeight: 600 }}
                              >
                                {order.customerDetails.phone}
                              </span>
                            )}
                            {order.customerDetails?.email && (
                              <span
                                style={{ display: "block", color: "#6b7280" }}
                              >
                                {order.customerDetails.email}
                              </span>
                            )}
                            {order.paymentMethod && (
                              <span
                                style={{
                                  display: "block",
                                  color: "#6b7280",
                                  marginTop: 2,
                                }}
                              >
                                💳 {order.paymentMethod}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Reorder */}
                      {order.orderStatus === "delivered" && (
                        <div style={{ padding: "0 16px 16px" }}>
                          <button
                            onClick={() => navigate("/products")}
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: 10,
                              border: "none",
                              cursor: "pointer",
                              background:
                                "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                              color: "#fff",
                              fontSize: 13,
                              fontWeight: 700,
                              boxShadow: "0 3px 10px rgba(45,139,78,0.22)",
                            }}
                          >
                            🔄 Order Again
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />

      {/* ── Cancel Order Modal ── */}
      {cancelModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setCancelModal(null)}
          />
          <div
            style={{
              position: "relative",
              background: "#fff",
              borderRadius: 18,
              padding: 24,
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{ fontSize: 36, textAlign: "center", marginBottom: 10 }}
            >
              ❌
            </div>
            <h3
              style={{
                fontSize: 17,
                fontWeight: 900,
                textAlign: "center",
                color: "#111827",
                margin: "0 0 4px",
              }}
            >
              Cancel Order
            </h3>
            <p
              style={{
                fontSize: 12,
                color: "#9ca3af",
                textAlign: "center",
                margin: "0 0 16px",
              }}
            >
              #{cancelModal.orderId} · ₹{cancelModal.total}
            </p>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 700,
                color: "#374151",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: ".4px",
              }}
            >
              Reason for cancellation *
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Ordered by mistake, wrong address, changed my mind…"
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1.5px solid rgba(239,68,68,0.25)",
                background: "#fff",
                fontSize: 13,
                color: "#111827",
                outline: "none",
                fontFamily: "inherit",
                resize: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#ef4444";
                e.target.style.boxShadow = "0 0 0 3px rgba(239,68,68,0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(239,68,68,0.25)";
                e.target.style.boxShadow = "none";
              }}
              autoFocus
            />
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button
                onClick={() => setCancelModal(null)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 10,
                  border: "1.5px solid #e5e7eb",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#6b7280",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Keep Order
              </button>
              <button
                onClick={handleCancel}
                disabled={
                  cancelling === cancelModal._id || !cancelReason.trim()
                }
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 10,
                  border: "none",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#fff",
                  background: "linear-gradient(135deg,#ef4444,#dc2626)",
                  cursor: "pointer",
                  opacity:
                    !cancelReason.trim() || cancelling === cancelModal._id
                      ? 0.5
                      : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {cancelling === cancelModal._id
                  ? "Cancelling…"
                  : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin       { to { transform: rotate(360deg); } }
        @keyframes blink      { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes pulse-ring { 0%{box-shadow:0 0 0 0 rgba(45,139,78,0.4)} 70%{box-shadow:0 0 0 6px rgba(45,139,78,0)} 100%{box-shadow:0 0 0 0 rgba(45,139,78,0)} }
        @media(max-width:480px) {
          div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
